'use client';

import React, { useMemo, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import type { DepartureLocationInfo, OrderStatus } from '@/entities/order/model/order.types';
import type { Marker, Arrow, RegionClickInfo } from '@/shared/ui/display/MapboxMap';
import { useCounselMapData } from './hooks/useCounselMapData';
import { CounselMapView } from './components/CounselMapView';

export interface CounselMapProps {
  /** 주문에서 파생된 visit counsel ID (parseVisitCounselId 결과) */
  visitCounselId: string | null;
  assignedDepartureLocation: DepartureLocationInfo | null | undefined;
  /** 출발지만 표시 시 fallback (counsels 없을 때) */
  fallbackOrder?: { id: string; misoOrderId: string } | null;
  orderStatus?: OrderStatus['type'];
  minHeight?: number;
  /** 컨테이너 높이를 채우도록 flex: 1 적용 */
  fill?: boolean;
  /** 스토리/테스트용: API 대신 이 데이터 사용 */
  counselsOverride?: Parameters<typeof useCounselMapData>[0]['counselsOverride'];
  /** 근무구역 경계 GeoJSON (플래너 배정 구역 등) */
  regionLayers?: GeoJSON.FeatureCollection[];
  /** 근무구역 표시명 (Chip/툴팁용, codePath 등) */
  regionLabels?: string[];
  /** 추가 마커 (접수됨 주문 등) - 기존 마커와 합쳐서 표시 */
  extraMarkers?: Marker[];
  /** 추가 화살표 (끼워넣기 라우트 등) - 기존 arrows와 합쳐서 표시 */
  extraArrows?: Arrow[];
  /** 마커 클릭 콜백 */
  onMarkerClick?: (markerId: string) => void;
  /** 선택된 구역 regionCode (이 구역만 원색, 나머지는 회색 처리) */
  selectedRootServiceRegionId?: string | null;
  /** 구역 클릭 콜백 (빈 곳 클릭 시 null) */
  onRegionClick?: (info: RegionClickInfo | null) => void;
  /** true면 마커 번호를 플래너별로 1,2,3... 매김 */
  numberingPerPlanner?: boolean;
  /** 설정 시 해당 플래너의 이동동선만 화살표로 표시 */
  selectedPlannerIdForArrows?: string | null;
  /** 팝업 링크 URL 오버라이드 (visitCounselId → URL) */
  markerLinkResolver?: (visitCounselId: string) => string | undefined;
  /** true면 마커 배지를 상담순서 대신 상담시간(13, 15½)으로 표시 (플래너 지도 뷰용) */
  viewByTime?: boolean;
  /** 시간/순서 토글 버튼 표시 (viewByTime과 함께 사용 시 시계 아이콘 노출) */
  onViewByTimeChange?: (checked: boolean) => void;
  /** false면 CounselMapSettings 미렌더 (상위에서 flex로 직접 배치 시) */
  renderSettings?: boolean;
  /** 플래너별 핀·경로 색 (상담 지도) */
  plannerColorById?: Record<string, string>;
  /** 지도 카메라: 이 값이 바뀔 때만 전체 fit (날짜·구역 로드 등). 필터만 바뀌면 동일 키 유지 */
  refitBoundsKey?: string | number;
  /** 마커 추가/삭제에도 카메라·지도 인스턴스 유지 */
  preserveCamera?: boolean;
}

/** 지도 데이터 props 변경 시에만 리렌더 */
function areCounselMapPropsEqual(prev: CounselMapProps, next: CounselMapProps): boolean {
  if (prev.visitCounselId !== next.visitCounselId) return false;
  if (prev.orderStatus !== next.orderStatus) return false;
  if (prev.minHeight !== next.minHeight) return false;
  if (prev.fill !== next.fill) return false;
  if (prev.onMarkerClick !== next.onMarkerClick) return false;
  if (prev.onRegionClick !== next.onRegionClick) return false;
  if (prev.selectedRootServiceRegionId !== next.selectedRootServiceRegionId) return false;
  if (prev.numberingPerPlanner !== next.numberingPerPlanner) return false;
  if (prev.selectedPlannerIdForArrows !== next.selectedPlannerIdForArrows) return false;
  if (prev.markerLinkResolver !== next.markerLinkResolver) return false;
  if (prev.fallbackOrder?.id !== next.fallbackOrder?.id) return false;
  if (prev.fallbackOrder?.misoOrderId !== next.fallbackOrder?.misoOrderId) return false;
  if (
    JSON.stringify(prev.assignedDepartureLocation) !==
    JSON.stringify(next.assignedDepartureLocation)
  )
    return false;
  if (JSON.stringify(prev.counselsOverride) !== JSON.stringify(next.counselsOverride)) return false;
  if (JSON.stringify(prev.regionLayers) !== JSON.stringify(next.regionLayers)) return false;
  if (JSON.stringify(prev.regionLabels) !== JSON.stringify(next.regionLabels)) return false;
  if (JSON.stringify(prev.extraMarkers) !== JSON.stringify(next.extraMarkers)) return false;
  if (JSON.stringify(prev.extraArrows) !== JSON.stringify(next.extraArrows)) return false;
  if (prev.viewByTime !== next.viewByTime) return false;
  if (prev.onViewByTimeChange !== next.onViewByTimeChange) return false;
  if (prev.renderSettings !== next.renderSettings) return false;
  if (JSON.stringify(prev.plannerColorById) !== JSON.stringify(next.plannerColorById)) return false;
  if (prev.refitBoundsKey !== next.refitBoundsKey) return false;
  if (prev.preserveCamera !== next.preserveCamera) return false;
  return true;
}

function CounselMapInner({
  visitCounselId,
  assignedDepartureLocation,
  fallbackOrder = null,
  orderStatus,
  minHeight = 150,
  fill = false,
  counselsOverride,
  regionLayers,
  regionLabels,
  extraMarkers,
  extraArrows,
  onMarkerClick,
  selectedRootServiceRegionId,
  onRegionClick,
  numberingPerPlanner = false,
  selectedPlannerIdForArrows = null,
  markerLinkResolver,
  viewByTime: viewByTimeProp,
  onViewByTimeChange: onViewByTimeChangeProp,
  renderSettings = true,
  plannerColorById,
  refitBoundsKey,
  preserveCamera,
}: CounselMapProps) {
  const [viewByTimeInternal, setViewByTime] = useState(viewByTimeProp ?? false);
  const viewByTime = viewByTimeProp ?? viewByTimeInternal;
  const onViewByTimeChange =
    onViewByTimeChangeProp ?? (viewByTimeProp === undefined ? setViewByTime : undefined);
  const { markers, arrows, isLoading, error } = useCounselMapData({
    visitCounselId,
    assignedDepartureLocation,
    fallbackOrder,
    orderStatus,
    counselsOverride,
    viewByTime,
    numberingPerPlanner,
    selectedPlannerIdForArrows,
    markerLinkResolver,
    plannerColorById,
  });

  const allMarkers = useMemo(() => {
    if (!extraMarkers?.length) return markers;
    return [...markers, ...extraMarkers];
  }, [markers, extraMarkers]);

  const allArrows = useMemo(() => {
    if (!extraArrows?.length) return arrows;
    return [...arrows, ...extraArrows];
  }, [arrows, extraArrows]);

  if (error) {
    return (
      <Box
        sx={{
          minHeight: minHeight ?? 150,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'error.main',
        }}
      >
        {error.message}
      </Box>
    );
  }

  if (isLoading && !counselsOverride) {
    return (
      <Box
        sx={{
          minHeight: minHeight ?? 150,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <CounselMapView
      markers={allMarkers}
      arrows={allArrows}
      regionLayers={regionLayers}
      regionLabels={regionLabels}
      selectedRootServiceRegionId={selectedRootServiceRegionId}
      onRegionClick={onRegionClick}
      minHeight={minHeight}
      fill={fill}
      viewByTime={viewByTime}
      onViewByTimeChange={onViewByTimeChange}
      onMarkerClick={onMarkerClick}
      renderSettings={renderSettings}
      refitBoundsKey={refitBoundsKey}
      preserveCamera={preserveCamera}
    />
  );
}

export const CounselMap = React.memo(CounselMapInner, areCounselMapPropsEqual);
